import React, { ChangeEvent, useState } from 'react';
import { memo } from 'react';
import { useRecoilState } from 'recoil';
import {
  changeUserAtom,
  textAlertErrorAtom,
} from '../../../models/atoms/account_atoms';
import { account_controller } from '../../../controllers/account_controller';
import { useTranslation } from '../../../contexts/TranslationContext';
import { DisclaimerText, ErrorText, MHMTypography } from '../MHM/MHMTypography';
import { Dialog, FormControlLabel, Stack, Switch } from '@mui/material';
import { MHMButton } from '../MHM/MHMButton';
import { SettingsCard } from './SettingsCard';
import { MHMDialog } from '../MHM/MHMDialog';
import { AlertTypeSettings } from './AlertTypeSettings';
/* eslint-disable no-useless-escape */

export const AlertSettingsComponent = memo(function AlertSettingsComponent(
  _: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >,
) {
  const [changeUser, setChangeUser] = useRecoilState(changeUserAtom);
  const [textAlertError, setTextAlertError] =
    useRecoilState(textAlertErrorAtom);
  const { t } = useTranslation();
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);

  function closeModal() {
    setIsSaveModalOpen(false);
  }

  function toggleEmailAlerts(e: ChangeEvent<HTMLInputElement>) {
    if (!changeUser) {
      return;
    }

    setChangeUser(prev =>
      prev
        ? {
            ...prev,
            change_alert_mediums: true,
            email_alerts: e.target.checked,
          }
        : prev,
    );
  }

  function toggleSMSAlerts(e: ChangeEvent<HTMLInputElement>) {
    if (!changeUser) {
      return;
    }

    if (
      !changeUser.phone_number ||
      changeUser.phone_number === 'Na' ||
      changeUser.phone_number.length < 10 ||
      !Number(changeUser.phone_number)
    ) {
      setTextAlertError(
        'Please provide a valid phone number in your account settings in order to receive text alerts!',
      );
      return;
    }

    setChangeUser(prev =>
      prev
        ? { ...prev, change_alert_mediums: true, text_alerts: e.target.checked }
        : prev,
    );
  }

  const handleSaveButtonClick = () => {
    if (!changeUser) {
      return;
    }

    const { email_alerts, text_alerts, alerts, change_alert_mediums } =
      changeUser;
    if (change_alert_mediums) {
      setIsSaveModalOpen(true);
    }

    account_controller.modify_user_account({
      text_alerts,
      email_alerts,
      alerts,
    });
  };

  if (!changeUser) {
    return null;
  }

  // At least one alert type should be enabled if email or SMS alerts are enabled
  const areAlertSettingsValid =
    changeUser.email_alerts || changeUser.text_alerts
      ? Object.values(changeUser.alerts).some(Boolean)
      : true;
  const canSave =
    areAlertSettingsValid &&
    (changeUser.change_alert_mediums || changeUser.change_alert_types);

  return (
    <>
      <SettingsCard id="alert" title={t('Alerts')} sx={{ overflow: 'unset' }}>
        <Stack gap={2} p={2}>
          <div>
            <DisclaimerText fontSize="small">
              {t('Control settings for notifications.')}
            </DisclaimerText>
            <DisclaimerText fontSize="small">
              {t(
                'The limit is one email and/or SMS notification per alert type per sensor per day.',
              )}
            </DisclaimerText>
            <DisclaimerText fontSize="small">
              {t(
                'Notifications are for the following alerts: high warning level, high ROC (if mode engaged), low battery, predictions for blockages, floods and silt build-ups, and weather mode activations.',
              )}
            </DisclaimerText>
          </div>

          <Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={changeUser.email_alerts}
                  onChange={toggleEmailAlerts}
                />
              }
              label={<MHMTypography medium>{t('Email')}</MHMTypography>}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={changeUser.text_alerts}
                  onChange={toggleSMSAlerts}
                />
              }
              label={<MHMTypography medium>{t('SMS')}</MHMTypography>}
            />
            {textAlertError && (
              <ErrorText fontSize="small">{t(textAlertError)}</ErrorText>
            )}
          </Stack>

          <AlertTypeSettings />

          {canSave && (
            <ErrorText fontSize="small">
              {t('Changes not yet saved.')}
            </ErrorText>
          )}

          <MHMButton
            disabled={!canSave}
            onClick={handleSaveButtonClick}
            sx={{ width: 'fit-content' }}
          >
            {t('Save')}
          </MHMButton>
        </Stack>
      </SettingsCard>

      <Dialog open={isSaveModalOpen} onClose={closeModal}>
        <MHMDialog.Header onClose={closeModal}>
          {t('Alert Settings Saved')}
        </MHMDialog.Header>

        <MHMDialog.Content>
          {t(
            'An email and/or SMS have been sent to the details\nprovided, please check your spam and ensure that alerts@manholemetrics.com is added to your contacts\n or safe sender list to prevent classification of alerts as spam.',
          )}
        </MHMDialog.Content>
      </Dialog>
    </>
  );
});
